(function (window) {
  if (window.TodayMerchProductOptionDrawer) return;

  const PANEL_TRANSITION_FALLBACK_MS = 260;

  class ProductOptionDrawer {
    constructor(config) {
      this.host = config?.host || null;
      this.trigger = config?.trigger || null;
      this.panel = config?.panel || null;
      this.details = config?.details || null;
      this.prefersReducedMotion = Boolean(config?.prefersReducedMotion);
      this.onOpen = typeof config?.onOpen === 'function' ? config.onOpen : null;
      this.onClose = typeof config?.onClose === 'function' ? config.onClose : null;
      this.onBeforeOpen = typeof config?.onBeforeOpen === 'function' ? config.onBeforeOpen : null;
      this.onBeforeClose = typeof config?.onBeforeClose === 'function' ? config.onBeforeClose : null;
      this.staticOpen = Boolean(config?.staticOpen);

      this.bound = {
        onTriggerClick: this.handleTriggerClick.bind(this),
        onDocumentClick: this.handleDocumentClick.bind(this),
        onDocumentKeydown: this.handleDocumentKeydown.bind(this),
      };
    }

    init(options = {}) {
      if (!this.host || !this.trigger || !this.panel) return false;

      this.abortController?.abort();
      this.abortController = new AbortController();

      const { signal } = this.abortController;
      const startsOpen = Boolean(
        this.staticOpen || options.startsOpen || this.details?.hasAttribute('open') || this.trigger.getAttribute('aria-expanded') === 'true'
      );

      if (this.panel.id) {
        this.trigger.setAttribute('aria-controls', this.panel.id);
      }

      this.trigger.addEventListener('click', this.bound.onTriggerClick, { signal });
      if (!this.staticOpen) {
        document.addEventListener('click', this.bound.onDocumentClick, { signal });
        document.addEventListener('keydown', this.bound.onDocumentKeydown, { signal });
      }

      this.host.classList.toggle('is-open', startsOpen);
      this.trigger.setAttribute('aria-expanded', startsOpen ? 'true' : 'false');
      this.panel.setAttribute('aria-hidden', startsOpen ? 'false' : 'true');
      this.panel.dataset.state = startsOpen ? 'open' : 'closed';
      this.panel.hidden = !startsOpen;
      this.panel.style.height = startsOpen ? 'auto' : '0px';
      this.setPanelInert(!startsOpen);

      if (this.details) {
        this.details.open = startsOpen;
      }

      if (startsOpen) {
        this.onOpen?.();
      }

      return true;
    }

    destroy() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.stopPanelAnimation();
    }

    isOpen() {
      return this.trigger?.getAttribute('aria-expanded') === 'true';
    }

    isPanelTransitioning() {
      return this.panel?.dataset.state === 'opening' || this.panel?.dataset.state === 'closing';
    }

    setOpen(open) {
      if (!this.host || !this.trigger || !this.panel) return;
      if (this.staticOpen && !open) {
        open = true;
      }
      if (open === this.isOpen() && !this.isPanelTransitioning()) return;

      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.host.classList.toggle('is-open', open);

      if (open) {
        this.onBeforeOpen?.();
      } else {
        this.onBeforeClose?.();
      }

      if (this.prefersReducedMotion) {
        if (this.details) {
          this.details.open = open;
        }

        this.panel.hidden = !open;
        this.panel.dataset.state = open ? 'open' : 'closed';
        this.panel.style.height = open ? 'auto' : '0px';
        this.panel.setAttribute('aria-hidden', open ? 'false' : 'true');
        this.setPanelInert(!open);

        if (open) {
          this.onOpen?.();
        } else {
          this.onClose?.();
        }

        return;
      }

      if (open) {
        this.openPanel();
      } else {
        this.closePanel();
      }
    }

    focusTrigger() {
      if (!this.trigger) return;

      try {
        this.trigger.focus({ preventScroll: true });
      } catch (error) {
        this.trigger.focus();
      }
    }

    handleTriggerClick(event) {
      event.preventDefault();
      if (this.staticOpen) {
        this.setOpen(true);
        return;
      }
      this.setOpen(!this.isOpen());
    }

    handleDocumentClick(event) {
      if (!this.isOpen()) return;

      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (path.includes(this.host)) return;
      if (event.target instanceof Node && this.host.contains(event.target)) return;

      this.setOpen(false);
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen()) return;

      event.preventDefault();
      this.setOpen(false);
      this.focusTrigger();
    }

    openPanel() {
      if (!this.panel) return;

      this.stopPanelAnimation();

      if (this.details) {
        this.details.open = true;
      }

      this.panel.hidden = false;
      this.panel.setAttribute('aria-hidden', 'false');
      this.setPanelInert(false);
      this.panel.dataset.state = 'opening';

      const startHeight = Math.max(0, Math.round(this.panel.getBoundingClientRect().height));
      const targetHeight = Math.max(this.panel.scrollHeight, startHeight);

      this.panel.style.height = `${startHeight}px`;
      this.panel.getBoundingClientRect();

      this.panelAnimationFrame = window.requestAnimationFrame(() => {
        this.panel.style.height = `${targetHeight}px`;
      });

      this.afterPanelTransition(() => {
        this.panel.dataset.state = 'open';
        this.panel.style.height = 'auto';
        this.onOpen?.();
      });
    }

    closePanel() {
      if (!this.panel) return;

      this.stopPanelAnimation();
      this.panel.dataset.state = 'closing';
      this.panel.setAttribute('aria-hidden', 'true');
      this.setPanelInert(true);

      const startHeight = Math.max(0, Math.round(this.panel.scrollHeight || this.panel.getBoundingClientRect().height));
      this.panel.style.height = `${startHeight}px`;
      this.panel.getBoundingClientRect();

      this.panelAnimationFrame = window.requestAnimationFrame(() => {
        this.panel.style.height = '0px';
      });

      this.afterPanelTransition(() => {
        this.panel.dataset.state = 'closed';
        this.panel.hidden = true;
        this.panel.style.height = '0px';

        if (this.details) {
          this.details.open = false;
        }

        this.onClose?.();
      });
    }

    setPanelInert(isInert) {
      if (this.panel && 'inert' in this.panel) {
        this.panel.inert = isInert;
      }
    }

    stopPanelAnimation() {
      if (!this.panel) return;

      if (this.panelAnimationFrame) {
        window.cancelAnimationFrame(this.panelAnimationFrame);
        this.panelAnimationFrame = undefined;
      }

      if (this.panelTransitionTimeout) {
        window.clearTimeout(this.panelTransitionTimeout);
        this.panelTransitionTimeout = undefined;
      }

      if (this.panelTransitionEndHandler) {
        this.panel.removeEventListener('transitionend', this.panelTransitionEndHandler);
        this.panelTransitionEndHandler = undefined;
      }
    }

    afterPanelTransition(callback) {
      if (!this.panel) return;

      const finalize = () => {
        if (this.panelTransitionTimeout) {
          window.clearTimeout(this.panelTransitionTimeout);
          this.panelTransitionTimeout = undefined;
        }

        if (this.panelTransitionEndHandler) {
          this.panel.removeEventListener('transitionend', this.panelTransitionEndHandler);
          this.panelTransitionEndHandler = undefined;
        }

        callback();
      };

      this.panelTransitionEndHandler = (event) => {
        if (event.target !== this.panel || event.propertyName !== 'height') return;
        finalize();
      };

      this.panel.addEventListener('transitionend', this.panelTransitionEndHandler);
      this.panelTransitionTimeout = window.setTimeout(finalize, PANEL_TRANSITION_FALLBACK_MS);
    }
  }

  window.TodayMerchProductOptionDrawer = {
    ProductOptionDrawer,
    create(config) {
      return new ProductOptionDrawer(config);
    },
  };
})(window);
